import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface DocumentType {
  id: string;
  name: string;
  form_number: string;
  description: string;
  category: string;
  scenario_count: number;
  user_scenarios_completed: number;
  mastery_level: string;
  questions_correct: number;
  questions_attempted: number;
}

interface GroupedDocuments {
  [category: string]: DocumentType[];
}

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<GroupedDocuments>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents/types-with-progress');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryTitle = (category: string): string => {
    const titles: { [key: string]: string } = {
      income: 'Income Documents',
      investment: 'Investment & Retirement',
      notices: 'IRS Correspondence',
      returns: 'Completed Returns'
    };
    return titles[category] || category.toUpperCase();
  };

  const getMasteryColor = (level: string): string => {
    switch (level) {
      case 'mastered':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'practicing':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const getMasteryBadge = (level: string): string => {
    switch (level) {
      case 'mastered':
        return '🏆 Mastered';
      case 'practicing':
        return '📚 Practicing';
      default:
        return '🌱 Learning';
    }
  };

  const getAccuracy = (doc: DocumentType): number => {
    if (doc.questions_attempted === 0) return 0;
    return Math.round((doc.questions_correct / doc.questions_attempted) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-xl text-white">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-primary-500/20 bg-slate-900/95 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">📄 Tax Documents & Forms</h1>
          <p className="text-slate-400">Practice reading and extracting data from real tax documents</p>
        </div>

        {/* Document Categories */}
        {Object.entries(documents).map(([category, docs]) => (
          <div key={category} className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">
              {getCategoryTitle(category)}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {docs.map((doc) => {
                const accuracy = getAccuracy(doc);
                const progress = doc.scenario_count > 0
                  ? Math.round((doc.user_scenarios_completed / doc.scenario_count) * 100)
                  : 0;

                return (
                  <div
                    key={doc.id}
                    onClick={() => navigate(`/documents/${doc.id}`)}
                    className="card hover:border-primary-500/50 transition-all cursor-pointer"
                  >
                    {/* Form Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">📋</span>
                      </div>
                      <div>
                        <div className="text-primary-400 text-sm font-semibold">
                          {doc.form_number}
                        </div>
                        <h3 className="text-lg font-bold">{doc.name}</h3>
                      </div>
                    </div>

                    <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                      {doc.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Progress</span>
                        <span className="text-white font-semibold">
                          {doc.user_scenarios_completed}/{doc.scenario_count} scenarios
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-primary-500 to-pink-500 h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>

                    {/* Accuracy */}
                    {doc.questions_attempted > 0 && (
                      <div className="text-sm text-slate-400 mb-3">
                        Accuracy: <span className="text-white font-semibold">{accuracy}%</span>
                        <span className="text-slate-500 ml-1">
                          ({doc.questions_correct}/{doc.questions_attempted})
                        </span>
                      </div>
                    )}

                    {/* Mastery Badge */}
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getMasteryColor(doc.mastery_level)}`}>
                      {getMasteryBadge(doc.mastery_level)}
                    </div>

                    {/* Action */}
                    <div className="mt-4 text-primary-400 text-sm font-semibold">
                      {doc.user_scenarios_completed === 0 ? 'Start Training →' : 'Continue Training →'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {Object.keys(documents).length === 0 && (
          <div className="text-center py-12">
            <div className="inline-block card max-w-2xl">
              <div className="text-4xl mb-4">🚧</div>
              <h3 className="text-xl font-bold mb-2">Document Training Modules Coming Soon</h3>
              <p className="text-slate-400 text-sm">
                We're preparing interactive document analysis exercises with real tax forms. 
                You'll be able to practice reading, analyzing, and extracting information from 
                actual tax documents to prepare for real-world tax preparation scenarios.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DocumentsPage;
